import { useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@shared/services";
import { THEME, TYPOGRAPHY } from "@shared/constants/designSystem";
import { useSiteGradients } from "@shared/hooks";
import { Button } from "@shared/components/base";
import { FormMessage } from "@shared/components/forms";

// Validation schema
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

function PasswordChangeForm({ onSuccess, onCancel }) {
  const [submitStatus, setSubmitStatus] = useState({ message: "", type: null });
  const gradients = useSiteGradients();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = async (data) => {
    try {
      setSubmitStatus({ message: "", type: null });

      // First verify current password by trying to sign in
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: data.currentPassword,
      });

      if (signInError) {
        throw new Error("Current password is incorrect");
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword,
      });

      if (updateError) throw updateError;

      setSubmitStatus({
        message: "Password changed successfully!",
        type: "success",
      });

      reset();

      // Call success callback after a delay
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (error) {
      setSubmitStatus({
        message: error.message || "Failed to change password",
        type: "error",
      });
    }
  };

  const inputClassName = `w-full px-3 py-2 border rounded-md ${THEME.COLORS.BORDER.DEFAULT} ${THEME.COLORS.BG.DEFAULT} ${THEME.COLORS.TEXT.DEFAULT} focus:outline-none focus:ring-2 focus:ring-primary-500`;
  const labelClassName = `block mb-2 ${TYPOGRAPHY.BODY.DEFAULT} ${THEME.COLORS.TEXT.DEFAULT}`;
  const errorClassName = `${THEME.COLORS.TEXT.MUTED} ${TYPOGRAPHY.BODY.SMALL} mt-1`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <FormMessage type={submitStatus.type} message={submitStatus.message} />

      <div>
        <label htmlFor="currentPassword" className={labelClassName}>
          Current Password
        </label>
        <input
          type="password"
          id="currentPassword"
          {...register("currentPassword")}
          className={`${inputClassName} ${errors.currentPassword ? "border-red-500" : ""}`}
          autoComplete="current-password"
        />
        {errors.currentPassword && (
          <p className={errorClassName}>{errors.currentPassword.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="newPassword" className={labelClassName}>
          New Password
        </label>
        <input
          type="password"
          id="newPassword"
          {...register("newPassword")}
          className={`${inputClassName} ${errors.newPassword ? "border-red-500" : ""}`}
          autoComplete="new-password"
        />
        {errors.newPassword && (
          <p className={errorClassName}>{errors.newPassword.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="confirmPassword" className={labelClassName}>
          Confirm New Password
        </label>
        <input
          type="password"
          id="confirmPassword"
          {...register("confirmPassword")}
          className={`${inputClassName} ${errors.confirmPassword ? "border-red-500" : ""}`}
          autoComplete="new-password"
        />
        {errors.confirmPassword && (
          <p className={errorClassName}>{errors.confirmPassword.message}</p>
        )}
      </div>

      <div className="flex gap-3 pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className={`${gradients.componentsButton} flex-1`}
        >
          {isSubmitting ? "Changing Password..." : "Change Password"}
        </Button>
      </div>
    </form>
  );
}

PasswordChangeForm.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default PasswordChangeForm;
